"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { trackTabView } from "@/lib/analytics/gtag";
import type { TabName } from "@/app/components/types";

const tabRoutes: Record<TabName, string> = {
  Dashboard: "/dashboard",
  Membership: "/membership",
  Visits: "/visits",
  Profile: "/profile",
};

interface TabNavigationProps {
  activeTab: TabName;
  tabs: TabName[];
  onLogoutRequest: () => void;
}

/**
 * @component TabNavigation
 * @description Desktop tab bar with full ARIA tablist support (arrow-key navigation,
 * Home/End, roving tabindex). Tracks tab views via Google Analytics. Syncs with URL
 * query parameter `tab` (e.g., ?tab=membership).
 * On mobile, displays only the active tab name when not on Dashboard.
 * Dynamic underline indicator that measures actual tab widths.
 * @param activeTab - Currently active tab.
 * @param onTabChange - Callback fired when the user selects a different tab.
 * @param onLogoutRequest - Callback fired when the user requests logout.
 * @returns A tab bar for desktop and a label indicator for mobile.
 */
export default function TabNavigation({
  tabs,
  activeTab,
  onLogoutRequest,
}: TabNavigationProps) {
  const tabListRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  useEffect(() => {
    trackTabView(activeTab);
  }, [activeTab]);

  // Update indicator position based on active tab element dimensions
  useEffect(() => {
    const updateIndicator = () => {
      if (!tabListRef.current) return;

      const activeButton = tabListRef.current.querySelector<HTMLButtonElement>(
        `[role="tab"][aria-selected="true"]`,
      );

      if (activeButton) {
        const navElement = tabListRef.current.querySelector("nav");
        if (navElement) {
          const navRect = navElement.getBoundingClientRect();
          const buttonRect = activeButton.getBoundingClientRect();

          setIndicatorStyle({
            left: buttonRect.left - navRect.left,
            width: buttonRect.width,
          });
        }
      }
    };

    updateIndicator();
    window.addEventListener("resize", updateIndicator);

    return () => window.removeEventListener("resize", updateIndicator);
  }, [activeTab]);

  const handleTabClick = (tab: TabName) => {
    router.push(tabRoutes[tab]);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    let nextIndex: number | null = null;

    if (e.key === "ArrowRight") {
      nextIndex = (index + 1) % tabs.length;
    } else if (e.key === "ArrowLeft") {
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = tabs.length - 1;
    }

    if (nextIndex !== null) {
      e.preventDefault();
      handleTabClick(tabs[nextIndex]);
      const buttons =
        tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      buttons?.[nextIndex]?.focus();
    }
  };

  return (
    <>
      {/* Desktop tab bar */}
      <div
        className="hidden lg:flex justify-between items-end mx-8 pt-6 bg-white sticky top-0 z-40 border-b border-border-subtle"
        role="tablist"
        aria-label="Dashboard sections"
        ref={tabListRef}
      >
        <nav className="relative flex space-x-8">
          {tabs.map((tabName, index) => (
            <button
              key={tabName}
              role="tab"
              id={`tab-${tabName}`}
              aria-selected={activeTab === tabName}
              aria-controls={`tabpanel-${tabName}`}
              tabIndex={activeTab === tabName ? 0 : -1}
              onClick={() => handleTabClick(tabName)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`text-label transition-colors duration-200 pb-3 ${
                activeTab === tabName ? "text-charcoal" : "text-stone"
              }`}
            >
              {tabName}
            </button>
          ))}
          <div
            className="absolute bottom-0 h-[2px] bg-accent-green transition-all duration-300 ease-in-out"
            style={{
              left: `${indicatorStyle.left}px`,
              width: `${indicatorStyle.width}px`,
            }}
          />
        </nav>
        {/* <button className="text-label text-stone hover:text-charcoal transition-colors pb-3"> */}
        <button
          className="pb-3 text-label transition-colors tracking-wide text-stone hover:text-charcoal"
          onClick={onLogoutRequest}
        >
          Log Out
        </button>
      </div>

      {/* Mobile: show active tab name with underline (only when not Dashboard) */}
      {activeTab !== "Dashboard" && (
        <div className="lg:hidden bg-shell px-4 pt-2 pb-0">
          <div className="border-b-2 border-accent-green inline-block pb-1">
            <span className="text-label text-charcoal">
              {activeTab.toUpperCase()}
            </span>
          </div>
        </div>
      )}
    </>
  );
}
